import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { TrendingUp, TrendingDown, Users, CheckCircle, AlertTriangle, MapPin, Clock, Activity, Filter, Download, Award, AlertCircle, BarChart3 } from 'lucide-react';
import * as XLSX from 'xlsx';
import styles from './BiometricDashboard.module.css';
import { getStudentCountV4, getAnalytics, getStudentDetails } from './services/admin.services';
import StudentDetailsTable from './components/CustomTable/StudentInfo';
import { ArrowLeft } from 'lucide-react';

const BiometricDashboard = () => {
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState('All');
  const [selectedMetric, setSelectedMetric] = useState('attendance');
  const [isLoading, setIsLoading] = useState(false);
  const [activeCenter, setActiveCenter] = useState(null);
  const [studentListData, setStudentListData] = useState([]);
  const [isStudentLoading, setIsStudentLoading] = useState(false);


  const [dashboardData, setDashboardData] = useState([]);

  const getSessionParam = (key) => {
    // Try direct key (e.g., 'examName')
    const direct = localStorage.getItem(key);
    if (direct) return direct;

    // Try parsing the examDetails object if it exists
    const details = JSON.parse(localStorage.getItem('examDetails') || '{}');
    return details[key] || details[key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)];
    // This last line converts examName -> exam_name automatically
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const examName = getSessionParam('examName');
      const shift = getSessionParam('shift');

      if (!examName || !shift) {
        console.warn("Session params missing:", { examName, shift });
        return;
      }

      const [countRes] = await Promise.all([
        getStudentCountV4(examName, shift)
      ]);

      console.log("Biometric Dashboard Raw API Response:", countRes.data);

      if (countRes.status === 200) {
        const mappedData = (countRes.data || []).map(item => {
          const total = Number(item.total_candidates || item.totalStudents || 0);
          const present = Math.abs(Number(item.present_count || item.presentStudents || 0));
          const attended = Math.abs(Number(item.attended_count || item.attendedStudents || 0));

          return {
            centerCode: item.center_code || item.centerCode,
            centerName: item.center_name || item.centerName,
            examCity: item.exam_city || item.examCity || "Local Center",
            totalStudents: total,
            presentStudents: present,
            attendedStudents: attended,
            shiftDate: item.shift_date || item.shiftDate,
            shiftTime: String(shift) === "3" ? "10:00 AM" : (item.shift_time || item.shiftTime),
            anyMismatched: present !== attended
          };
        });
        setDashboardData(mappedData);
      }
    } catch (error) {
      console.error("Dashboard API Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCenterClick = async (center) => {
    setActiveCenter(center);
    setIsStudentLoading(true);
    try {
      const examName = getSessionParam('examName');
      const shift = getSessionParam('shift');
      // Correct order: Exam Name, Center Code, Shift
      const response = await getStudentDetails(examName, center.centerCode, shift);

      if (response.status === 200) {
        // Mapping layer to handle potential key differences and structure for StudentDetailsTable
        const mappedStudents = (response.data || []).map(s => ({
          ...s,
          // Handle common variations to ensure StudentDetailsTable displays data correctly
          name: s.name || s.student_name || s.candidate_name || "N/A",
          rollNumber: s.rollNumber || s.roll_no || s.roll_number || "N/A",
          photo: s.photo || s.candidate_photo || s.image_url,
          enrollmentStatus: {
            ...(s.enrollmentStatus || {}),
            isPresent: s.enrollmentStatus?.isPresent ?? s.is_present ?? s.attendance_status === 'Present',
            userEnrolled: s.enrollmentStatus?.userEnrolled ?? s.is_enrolled ?? s.enrolled,
            enrollmentTime: s.enrollmentStatus?.enrollmentTime ?? s.enrollment_time ?? s.timestamp,
          },
          enrollmentData: {
            ...(s.enrollmentData || {}),
            newPicture: s.enrollmentData?.newPicture ?? s.live_photo ?? s.current_image,
            thumbPicture: s.enrollmentData?.thumbPicture ?? s.fingerprint_image ?? s.thumb_photo,
            similarityImage: s.enrollmentData?.similarityImage ?? s.similarity_score ?? s.match_percentage
          }
        }));
        setStudentListData(mappedStudents);
      }
    } catch (error) {
      console.error("Error fetching student details:", error);
    } finally {
      setIsStudentLoading(false);
    }
  };

  const handleExport = () => {
    const exportData = filteredData.map(center => {
      const rate = center.totalStudents > 0 ? ((center.presentStudents / center.totalStudents) * 100).toFixed(1) : 0;
      return {
        'Center Code': center.centerCode,
        'Center Name': center.centerName,
        'City': center.examCity,
        'Total': center.totalStudents,
        'Present': center.presentStudents,
        'Attended': center.attendedStudents,
        'Rate (%)': `${rate}%`,
        'Status': center.anyMismatched ? 'Mismatch' : 'Synced'
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Biometric Analytics");
    XLSX.writeFile(workbook, "Biometric_Analytics_Report.xlsx");
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = selectedCity === 'All'
    ? dashboardData
    : dashboardData.filter(c => c.examCity === selectedCity);

  const cities = ['All', ...new Set(dashboardData.map(c => c.examCity))];

  // Analytics calculations
  const stats = useMemo(() => {
    const totalCandidates = filteredData.reduce((sum, c) => sum + c.totalStudents, 0);
    const totalPresent = filteredData.reduce((sum, c) => sum + c.presentStudents, 0);
    const totalAttended = filteredData.reduce((sum, c) => sum + c.attendedStudents, 0);
    const centersStarted = filteredData.filter(c => c.presentStudents > 0).length;
    const mismatchedCenters = filteredData.filter(c => c.anyMismatched).length;

    return {
      totalCandidates,
      totalPresent,
      totalAttended,
      attendanceRate: totalCandidates > 0 ? ((totalAttended / totalCandidates) * 100).toFixed(2) : 0,
      syncRate: totalCandidates > 0 ? ((totalPresent / totalCandidates) * 100).toFixed(2) : 0,
      enrollmentRate: ((centersStarted / filteredData.length) * 100).toFixed(2),
      centersStarted,
      totalCenters: filteredData.length,
      mismatchedCenters
    };
  }, [filteredData]);

  // Top performing centers
  const topCenters = useMemo(() => {
    return [...filteredData]
      .sort((a, b) => {
        const rateA = a.totalStudents > 0 ? (a.presentStudents / a.totalStudents) : 0;
        const rateB = b.totalStudents > 0 ? (b.presentStudents / b.totalStudents) : 0;
        return rateB - rateA;
      })
      .slice(0, 3);
  }, [filteredData]);

  // Lowest performing centers (need attention)
  const lowestCenters = useMemo(() => {
    return [...filteredData]
      .sort((a, b) => {
        const rateA = a.totalStudents > 0 ? (a.presentStudents / a.totalStudents) : 0;
        const rateB = b.totalStudents > 0 ? (b.presentStudents / b.totalStudents) : 0;
        return rateA - rateB;
      })
      .slice(0, 3);
  }, [filteredData]);

  // Additional analytics
  const advancedAnalytics = useMemo(() => {
    const centersWithFullAttendance = filteredData.filter(c => c.presentStudents === c.totalStudents).length;
    const centersWithNoAttendance = filteredData.filter(c => c.presentStudents === 0).length;
    const avgAttendancePerCenter = filteredData.length > 0
      ? (filteredData.reduce((sum, c) => sum + c.presentStudents, 0) / filteredData.length).toFixed(1)
      : 0;
    const highestAttendanceCenter = [...filteredData].sort((a, b) => b.presentStudents - a.presentStudents)[0];
    const totalAbsent = filteredData.reduce((sum, c) => sum + (c.totalStudents - c.presentStudents), 0);
    const syncSuccessRate = filteredData.filter(c => !c.anyMismatched).length / filteredData.length * 100;

    return {
      centersWithFullAttendance,
      centersWithNoAttendance,
      avgAttendancePerCenter,
      highestAttendanceCenter,
      totalAbsent,
      syncSuccessRate: syncSuccessRate.toFixed(1)
    };
  }, [filteredData]);

  // Chart data
  const cityPerformance = useMemo(() => {
    const cityMap = {};
    dashboardData.forEach(c => {
      if (!cityMap[c.examCity]) {
        cityMap[c.examCity] = { city: c.examCity, present: 0, total: 0, attended: 0 };
      }
      cityMap[c.examCity].present += c.presentStudents;
      cityMap[c.examCity].total += c.totalStudents;
      cityMap[c.examCity].attended += c.attendedStudents;
    });
    return Object.values(cityMap).map(c => ({
      ...c,
      rate: c.total > 0 ? ((c.present / c.total) * 100).toFixed(1) : 0
    }));
  }, [dashboardData]);

  const statusDistribution = useMemo(() => {
    const synced = filteredData.filter(c => !c.anyMismatched).length;
    const mismatched = filteredData.filter(c => c.anyMismatched).length;
    return [
      { name: 'Synced', value: synced, color: '#10b981' },
      { name: 'Mismatched', value: mismatched, color: '#f59e0b' }
    ];
  }, [filteredData]);

  const centerComparisonData = useMemo(() => {
    return filteredData.map(c => ({
      name: c.centerCode,
      Present: c.presentStudents,
      Absent: c.totalStudents - c.presentStudents,
      Attended: c.attendedStudents,
      rate: c.totalStudents > 0 ? ((c.presentStudents / c.totalStudents) * 100).toFixed(1) : 0
    }));
  }, [filteredData]);

  const performanceRadar = useMemo(() => {
    const avgPresent = filteredData.reduce((sum, c) => sum + c.presentStudents, 0) / filteredData.length;
    const avgAttended = filteredData.reduce((sum, c) => sum + c.attendedStudents, 0) / filteredData.length;
    const avgSync = filteredData.filter(c => !c.anyMismatched).length / filteredData.length * 100;

    return [
      { metric: 'Attendance', value: parseFloat(stats.attendanceRate) },
      { metric: 'Sync Rate', value: parseFloat(stats.syncRate) },
      { metric: 'Enrollment', value: parseFloat(stats.enrollmentRate) },
      { metric: 'Completion', value: avgSync },
      { metric: 'Efficiency', value: (avgPresent / 15 * 100) }
    ];
  }, [filteredData, stats]);

  const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];

  return (
    <div className={`${styles.dashboardContainer} w-full overflow-x-hidden text-slate-900 p-6 lg:pl-6`}>
      {isLoading && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-sm z-50 flex items-center justify-center flex-col gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-blue-600 font-bold animate-pulse">Fetching Live Analytics...</p>
        </div>
      )}

      {/* Header with Logo */}
      {!activeCenter ? (
        <>
          <div className={`${styles.fadeIn} mb-8`}>
            <div className={`${styles.glassStrong} rounded-3xl p-6 mb-6`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  {/* Logo Area */}
                  <div className={`${styles.logoArea} p-6 rounded-2xl flex items-center justify-center w-24 h-24 shadow-lg`}>
                    <div className="text-white text-center">
                      <div className="text-3xl font-bold">B</div>
                      <div className="text-xs font-medium">CORE</div>
                    </div>
                  </div>
                  <div>
                    <h1 className={`text-5xl font-bold mb-2 ${styles.gradientText}`}>Biometric Analytics</h1>
                    <p className="text-slate-600 text-lg">Real-time examination monitoring system</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleExport}
                    className="glass px-6 py-3 rounded-2xl hover:bg-white transition-all flex items-center gap-2 font-medium text-slate-700 border-2 border-blue-200"
                  >
                    <Download size={20} />
                    Export
                  </button>
                  <button className={`bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 rounded-2xl hover:from-blue-500 hover:to-indigo-500 transition-all flex items-center gap-2 font-medium text-white ${styles.glow}`}>
                    <Activity size={20} />
                    Live Data
                  </button>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className={`${styles.glassStrong} rounded-2xl p-4 flex items-center gap-4`}>
              <Filter size={20} className="text-blue-600" />
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="bg-white border-2 border-blue-200 rounded-xl px-4 py-2 text-slate-700 outline-none focus:border-blue-500 transition-colors"
              >
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>

              <div className="flex gap-2 ml-auto">
                <button
                  onClick={() => setSelectedMetric('attendance')}
                  className={`px-4 py-2 rounded-xl transition-all font-medium ${selectedMetric === 'attendance' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                >
                  Attendance
                </button>
                <button
                  onClick={() => setSelectedMetric('sync')}
                  className={`px-4 py-2 rounded-xl transition-all font-medium ${selectedMetric === 'sync' ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}
                >
                  Sync Status
                </button>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className={`${styles.glassStrong} ${styles.statCard} rounded-2xl p-6 ${styles.fadeIn} border-l-4 border-blue-600`} style={{ animationDelay: '0.1s' }}>
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl text-white">
                  <Users size={24} />
                </div>
                <span className={`text-xs ${styles.mono} bg-blue-100 px-3 py-1 rounded-full text-blue-700 font-semibold`}>TOTAL</span>
              </div>
              <h3 className="text-4xl font-bold mb-1 text-slate-900">{stats.totalCandidates}</h3>
              <p className="text-slate-600 text-sm font-medium">Total Candidates</p>
              <div className="mt-3 flex items-center gap-2 text-sm">
                <TrendingUp size={16} className="text-green-600" />
                <span className="text-green-600 font-semibold">{stats.totalPresent} Present</span>
              </div>
            </div>

            <div className={`${styles.glassStrong} ${styles.statCard} rounded-2xl p-6 ${styles.fadeIn} border-l-4 border-indigo-600`} style={{ animationDelay: '0.2s' }}>
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-xl text-white">
                  <CheckCircle size={24} />
                </div>
                <span className={`text-xs ${styles.mono} bg-indigo-100 px-3 py-1 rounded-full text-indigo-700 font-semibold`}>RATE</span>
              </div>
              <h3 className="text-4xl font-bold mb-1 text-slate-900">{stats.attendanceRate}%</h3>
              <p className="text-slate-600 text-sm font-medium">Attendance Rate</p>
              <div className="mt-3 w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-600 to-blue-600 transition-all duration-1000"
                  style={{ width: `${stats.attendanceRate}%` }}
                />
              </div>
            </div>

            <div className={`${styles.glassStrong} ${styles.statCard} rounded-2xl p-6 ${styles.fadeIn} border-l-4 border-cyan-600`} style={{ animationDelay: '0.3s' }}>
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-xl text-white">
                  <Activity size={24} />
                </div>
                <span className={`text-xs ${styles.mono} bg-cyan-100 px-3 py-1 rounded-full text-cyan-700 font-semibold`}>SYNC</span>
              </div>
              <h3 className="text-4xl font-bold mb-1 text-slate-900">{stats.syncRate}%</h3>
              <p className="text-slate-600 text-sm font-medium">Sync Rate</p>
              <div className="mt-3 flex items-center gap-2 text-sm">
                <span className="text-slate-600 font-medium">{stats.totalPresent}/{stats.totalCandidates}</span>
              </div>
            </div>

            <div className={`${styles.glassStrong} ${styles.statCard} rounded-2xl p-6 ${styles.fadeIn} border-l-4 border-blue-500`} style={{ animationDelay: '0.4s' }}>
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl text-white">
                  <MapPin size={24} />
                </div>
                <span className={`text-xs ${styles.mono} bg-blue-100 px-3 py-1 rounded-full text-blue-700 font-semibold`}>CENTERS</span>
              </div>
              <h3 className="text-4xl font-bold mb-1 text-slate-900">{stats.centersStarted}/{stats.totalCenters}</h3>
              <p className="text-slate-600 text-sm font-medium">Centers Active</p>
              <div className="mt-3 flex items-center gap-2 text-sm">
                {stats.mismatchedCenters > 0 && (
                  <>
                    <AlertTriangle size={16} className="text-amber-600" />
                    <span className="text-amber-600 font-semibold">{stats.mismatchedCenters} Issues</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Advanced Analytics Insights */}
          <div className={`${styles.glassStrong} rounded-2xl p-6 mb-8 ${styles.fadeIn}`} style={{ animationDelay: '0.45s' }}>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-900">
              <div className="w-1 h-8 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full" />
              Advanced Analytics Insights
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="text-sm font-semibold text-green-800">Full Attendance</span>
                </div>
                <p className="text-3xl font-bold text-green-900">{advancedAnalytics.centersWithFullAttendance}</p>
                <p className="text-xs text-green-700 mt-1">Centers with 100% present</p>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-4 border-2 border-red-200">
                <div className="flex items-center gap-3 mb-2">
                  <AlertCircle className="text-red-600" size={20} />
                  <span className="text-sm font-semibold text-red-800">No Attendance</span>
                </div>
                <p className="text-3xl font-bold text-red-900">{advancedAnalytics.centersWithNoAttendance}</p>
                <p className="text-xs text-red-700 mt-1">Centers with 0% present</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200">
                <div className="flex items-center gap-3 mb-2">
                  <BarChart3 className="text-blue-600" size={20} />
                  <span className="text-sm font-semibold text-blue-800">Avg Per Center</span>
                </div>
                <p className="text-3xl font-bold text-blue-900">{advancedAnalytics.avgAttendancePerCenter}</p>
                <p className="text-xs text-blue-700 mt-1">Average students present</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4 border-2 border-purple-200">
                <div className="flex items-center gap-3 mb-2">
                  <Award className="text-purple-600" size={20} />
                  <span className="text-sm font-semibold text-purple-800">Sync Success</span>
                </div>
                <p className="text-3xl font-bold text-purple-900">{advancedAnalytics.syncSuccessRate}%</p>
                <p className="text-xs text-purple-700 mt-1">Centers synced properly</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-amber-800 mb-1">Total Absent Candidates</p>
                    <p className="text-3xl font-bold text-amber-900">{advancedAnalytics.totalAbsent}</p>
                    <p className="text-xs text-amber-700 mt-1">Candidates not present today</p>
                  </div>
                  <TrendingDown className="text-amber-600" size={40} />
                </div>
              </div>

              {advancedAnalytics.highestAttendanceCenter && (
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 border-2 border-cyan-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-cyan-800 mb-1">Highest Attendance</p>
                      <p className="text-xl font-bold text-cyan-900">{advancedAnalytics.highestAttendanceCenter.centerName}</p>
                      <p className="text-xs text-cyan-700 mt-1">{advancedAnalytics.highestAttendanceCenter.presentStudents} students present</p>
                    </div>
                    <Award className="text-cyan-600" size={40} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* City Performance */}
            <div className={`lg:col-span-2 ${styles.glassStrong} rounded-2xl p-6 ${styles.fadeIn}`} style={{ animationDelay: '0.5s' }}>
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-900">
                <div className="w-1 h-8 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full" />
                City-wise Performance
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={cityPerformance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="city" stroke="#64748b" tick={{ fill: '#64748b' }} />
                  <YAxis stroke="#64748b" tick={{ fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      border: '2px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                    labelStyle={{ color: '#1e293b', fontWeight: 'bold' }}
                  />
                  <Legend wrapperStyle={{ color: '#64748b' }} />
                  <Bar dataKey="present" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="total" fill="#6366f1" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Status Distribution */}
            <div className={`${styles.glassStrong} rounded-2xl p-6 ${styles.fadeIn}`} style={{ animationDelay: '0.6s' }}>
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-900">
                <div className="w-1 h-8 bg-gradient-to-b from-green-600 to-blue-600 rounded-full" />
                Sync Status
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      border: '2px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-around mt-4">
                {statusDistribution.map(item => (
                  <div key={item.name} className="text-center">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-slate-600 font-medium">{item.name}</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Center Comparison & Performance Radar */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Center Comparison */}
            <div className={`${styles.glassStrong} rounded-2xl p-6 ${styles.fadeIn}`} style={{ animationDelay: '0.7s' }}>
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-900">
                <div className="w-1 h-8 bg-gradient-to-b from-cyan-600 to-blue-600 rounded-full" />
                Center Comparison
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={centerComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="name" stroke="#64748b" tick={{ fill: '#64748b' }} />
                  <YAxis stroke="#64748b" tick={{ fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      border: '2px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend wrapperStyle={{ color: '#64748b' }} />
                  <Line type="monotone" dataKey="Present" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', r: 5 }} />
                  <Line type="monotone" dataKey="Attended" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Performance Radar */}
            <div className={`${styles.glassStrong} rounded-2xl p-6 ${styles.fadeIn}`} style={{ animationDelay: '0.8s' }}>
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-900">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full" />
                Performance Metrics
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={performanceRadar}>
                  <PolarGrid stroke="rgba(0,0,0,0.1)" />
                  <PolarAngleAxis dataKey="metric" stroke="#64748b" tick={{ fill: '#64748b' }} />
                  <PolarRadiusAxis stroke="#64748b" />
                  <Radar name="Performance" dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.6} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      border: '2px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Performing Centers */}
          <div className={`${styles.glassStrong} rounded-2xl p-6 ${styles.fadeIn} mb-8`} style={{ animationDelay: '0.9s' }}>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-900">
              <div className="w-1 h-8 bg-gradient-to-b from-emerald-600 to-teal-600 rounded-full" />
              Top Performing Centers
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {topCenters.map((center, idx) => {
                const rate = center.totalStudents > 0 ? ((center.presentStudents / center.totalStudents) * 100).toFixed(1) : 0;
                const medals = ['🥇', '🥈', '🥉'];

                return (
                  <div key={center.centerCode} className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 hover:shadow-xl transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-4xl">{medals[idx]}</span>
                      <span className={`${styles.mono} text-xs bg-green-600 text-white px-3 py-1 rounded-full font-semibold`}>{center.centerCode}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-slate-900">{center.centerName}</h3>
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                      <MapPin size={14} />
                      <span>{center.examCity}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                      <Clock size={14} />
                      <span>{center.shiftTime}</span>
                    </div>
                    <div className="pt-4 border-t border-green-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-slate-600 font-medium">Performance</span>
                        <span className="text-2xl font-bold text-green-700">{rate}%</span>
                      </div>
                      <div className="w-full bg-green-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-600 to-teal-600 transition-all duration-1000"
                          style={{ width: `${rate}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-3 text-xs">
                        <span className="text-slate-600">Present: <span className="text-slate-900 font-semibold">{center.presentStudents}</span></span>
                        <span className="text-slate-600">Total: <span className="text-slate-900 font-semibold">{center.totalStudents}</span></span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Lowest Performing Centers - Need Attention */}
          <div className={`${styles.glassStrong} rounded-2xl p-6 ${styles.fadeIn} mb-8`} style={{ animationDelay: '0.95s' }}>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-900">
              <div className="w-1 h-8 bg-gradient-to-b from-red-600 to-orange-600 rounded-full" />
              Centers Needing Attention
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {lowestCenters.map((center, idx) => {
                const rate = center.totalStudents > 0 ? ((center.presentStudents / center.totalStudents) * 100).toFixed(1) : 0;
                const icons = [
                  <AlertTriangle key={idx} size={32} className="text-red-600" />,
                  <AlertCircle key={idx} size={32} className="text-orange-600" />,
                  <TrendingDown key={idx} size={32} className="text-amber-600" />
                ];

                return (
                  <div key={center.centerCode} className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6 hover:shadow-xl transition-all">
                    <div className="flex items-start justify-between mb-4">
                      {icons[idx]}
                      <span className={`${styles.mono} text-xs bg-red-600 text-white px-3 py-1 rounded-full font-semibold`}>{center.centerCode}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-slate-900">{center.centerName}</h3>
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                      <MapPin size={14} />
                      <span>{center.examCity}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                      <Clock size={14} />
                      <span>{center.shiftTime}</span>
                    </div>
                    <div className="pt-4 border-t border-red-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-slate-600 font-medium">Performance</span>
                        <span className="text-2xl font-bold text-red-700">{rate}%</span>
                      </div>
                      <div className="w-full bg-red-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-600 to-orange-600 transition-all duration-1000"
                          style={{ width: `${rate}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-3 text-xs">
                        <span className="text-slate-600">Present: <span className="text-slate-900 font-semibold">{center.presentStudents}</span></span>
                        <span className="text-slate-600">Total: <span className="text-slate-900 font-semibold">{center.totalStudents}</span></span>
                      </div>
                      {center.anyMismatched && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-100 px-3 py-1 rounded-lg">
                          <AlertTriangle size={12} />
                          <span className="font-semibold">Sync Issues Detected</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`${styles.glassStrong} rounded-2xl p-6 ${styles.fadeIn}`} style={{ animationDelay: '1s' }}>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-900">
              <div className="w-1 h-8 bg-gradient-to-b from-violet-600 to-blue-600 rounded-full" />
              Center Details
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-blue-200 bg-blue-50">
                    <th className="text-left py-4 px-4 text-slate-700 font-semibold cursor-default">Code</th>
                    <th className="text-left py-4 px-4 text-slate-700 font-semibold cursor-default">Center Name</th>
                    <th className="text-left py-4 px-4 text-slate-700 font-semibold cursor-default">City</th>
                    <th className="text-center py-4 px-4 text-slate-700 font-semibold cursor-default">Total</th>
                    <th className="text-center py-4 px-4 text-slate-700 font-semibold cursor-default">Present</th>
                    <th className="text-center py-4 px-4 text-slate-700 font-semibold cursor-default">Attended</th>
                    <th className="text-center py-4 px-4 text-slate-700 font-semibold border-r border-blue-100 cursor-default">Rate</th>
                    <th className="text-left py-4 px-4 text-slate-700 font-semibold border-r border-blue-100 cursor-default">Sync Health</th>
                    <th className="text-center py-4 px-4 text-slate-700 font-semibold cursor-default">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((center, idx) => {
                    const rate = center.totalStudents > 0 ? ((center.presentStudents / center.totalStudents) * 100).toFixed(1) : 0;
                    return (
                      <tr
                        key={center.centerCode}
                        onClick={() => handleCenterClick(center)}
                        className="border-b border-slate-200 hover:bg-white/10 transition-colors cursor-pointer group"
                      >
                        <td className={`py-4 px-4 ${styles.mono} text-sm font-semibold text-slate-900`}>{center.centerCode}</td>
                        <td className="py-4 px-4 font-medium text-slate-900 group-hover:text-blue-600 transition-colors">{center.centerName}</td>
                        <td className="py-4 px-4 text-slate-600">{center.examCity}</td>
                        <td className={`py-4 px-4 text-center ${styles.mono} text-slate-900`}>{center.totalStudents}</td>
                        <td className={`py-4 px-4 text-center ${styles.mono} font-semibold text-blue-600`}>{center.presentStudents}</td>
                        <td className={`py-4 px-4 text-center ${styles.mono} font-semibold text-emerald-600`}>{center.attendedStudents}</td>
                        <td className="py-4 px-4 text-center border-r border-slate-100">
                          <span className={`${styles.mono} font-bold ${rate > 50 ? 'text-green-600' : rate > 20 ? 'text-amber-600' : 'text-red-600'}`}>
                            {rate}%
                          </span>
                        </td>
                        <td className="py-4 px-4 border-r border-slate-100">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${styles.pulse} ${!center.anyMismatched ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                              <span className="text-xs font-bold text-slate-700">
                                {!center.anyMismatched ? 'ONLINE' : 'OFFLINE'}
                              </span>
                            </div>
                            <span className="text-[10px] m-0 leading-tight font-mono text-slate-500 font-medium">
                              Pending: <span className="text-indigo-600 font-bold">{center.anyMismatched ? 45 : 0}</span>
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/dashboard/device-monitor');
                            }}
                            className="text-xs font-bold text-blue-600 border-2 border-blue-200 hover:bg-blue-50 px-3 py-1 rounded-lg transition-all"
                          >
                            Diagnostics
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className={`${styles.fadeIn} relative`}>
          {/* Prominent Back Button - Top Left */}
          <div className="mb-6">
            <button
              onClick={() => setActiveCenter(null)}
              className="group flex items-center gap-3 px-5 py-2.5 bg-white/40 backdrop-blur-xl border border-white/40 rounded-2xl text-slate-900 hover:bg-white/60 transition-all font-bold shadow-lg hover:shadow-blue-500/10"
            >
              <div className="p-1 px-2.5 bg-blue-600 rounded-lg text-white group-hover:scale-110 transition-transform">
                <ArrowLeft size={18} strokeWidth={3} />
              </div>
              <span>Back to Analytics</span>
            </button>
          </div>

          <div className="flex items-center justify-between mb-8 px-2">
            <div>
              <h2 className="text-4xl font-black text-slate-900 tracking-tight">{activeCenter.centerName}</h2>
              <div className="flex items-center gap-4 mt-2">
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold font-mono uppercase tracking-wider">
                  {activeCenter.centerCode}
                </span>
                <span className="flex items-center gap-1.5 text-slate-500 font-medium">
                  <MapPin size={16} />
                  {activeCenter.examCity}
                </span>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="text-right">
                <p className="text-xs font-bold text-slate-400 mb-1 uppercase tracking-widest leading-none">Status</p>
                <div className="flex items-center gap-2 justify-end">
                  <span className={`w-3 h-3 rounded-full ${styles.pulse} ${!activeCenter.anyMismatched ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                  <span className="text-lg font-bold text-slate-800">
                    {!activeCenter.anyMismatched ? 'ONLINE SYNCED' : 'OFFLINE PENDING'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            className="rounded-3xl overflow-hidden shadow-2xl"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <StudentDetailsTable
              studentData={studentListData}
              loading={isStudentLoading}
              isAdmin={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BiometricDashboard;
